import React, { useEffect, useMemo, useState } from 'react';
import './GlobalDataTable.css';

const getAlignmentClass = (align = 'left') => {
  if (align === 'center') return 'global-table-cell--center';
  if (align === 'right') return 'global-table-cell--right';
  return 'global-table-cell--left';
};

const resolveRowKey = (row, index, rowKey) => {
  if (typeof rowKey === 'function') {
    return rowKey(row, index);
  }

  if (typeof rowKey === 'string' && row?.[rowKey] !== undefined) {
    return row[rowKey];
  }

  return index;
};

const GlobalDataTable = ({
  columns = [],
  rows = [],
  rowKey,
  emptyMessage = 'No hay datos para mostrar',
  emptyIcon,
  className = '',
  enablePagination = true,
  rowsPerPage = 10,
  currentPage,
  onPageChange,
  totalItems,
  paginationMode = 'client',
  pageResetKey,
}) => {
  const [internalPage, setInternalPage] = useState(1);

  const isControlledPagination =
    typeof currentPage === 'number' && typeof onPageChange === 'function';

  const activePage = isControlledPagination ? currentPage : internalPage;

  const safeRowsPerPage = rowsPerPage > 0 ? rowsPerPage : 10;

  const totalRows = useMemo(() => {
    if (paginationMode === 'server') {
      if (typeof totalItems === 'number' && Number.isFinite(totalItems)) {
        return totalItems;
      }
      return rows.length;
    }

    return rows.length;
  }, [paginationMode, totalItems, rows]);

  const totalPages = Math.max(1, Math.ceil(totalRows / safeRowsPerPage));
  const normalizedPage = Math.min(Math.max(activePage, 1), totalPages);

  const setPage = (page) => {
    const nextPage = Math.min(Math.max(page, 1), totalPages);

    if (isControlledPagination) {
      onPageChange(nextPage);
      return;
    }

    setInternalPage(nextPage);
  };

  useEffect(() => {
    if (!enablePagination) return;
    if (activePage === normalizedPage) return;
    setPage(normalizedPage);
  }, [enablePagination, activePage, normalizedPage]);

  useEffect(() => {
    if (!enablePagination) return;
    if (pageResetKey === undefined) return;
    setPage(1);
  }, [enablePagination, pageResetKey]);

  const visibleRows = useMemo(() => {
    if (!enablePagination || paginationMode === 'server') {
      return rows;
    }

    const start = (normalizedPage - 1) * safeRowsPerPage;
    const end = start + safeRowsPerPage;
    return rows.slice(start, end);
  }, [enablePagination, paginationMode, rows, normalizedPage, safeRowsPerPage]);

  const rangeStart = totalRows === 0 ? 0 : (normalizedPage - 1) * safeRowsPerPage + 1;
  const rangeEnd = Math.min(normalizedPage * safeRowsPerPage, totalRows);

  return (
    <div className={`global-table-card ${className}`.trim()}>
      <div className="global-table-scroll">
        <table className="global-table">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key || column.header}
                  className={`${getAlignmentClass(column.align)} ${column.headerClassName || ''}`.trim()}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.length > 0 ? (
              visibleRows.map((row, index) => (
                <tr
                  key={resolveRowKey(row, index, rowKey)}
                  className={typeof row?.rowClassName === 'string' ? row.rowClassName : ''}
                >
                  {columns.map((column) => (
                    <td
                      key={`${column.key || column.header}-${resolveRowKey(row, index, rowKey)}`}
                      className={`${getAlignmentClass(column.align)} ${column.cellClassName || ''}`.trim()}
                    >
                      {typeof column.render === 'function'
                        ? column.render(row, index)
                        : row?.[column.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={Math.max(columns.length, 1)} className="global-table-empty">
                  {emptyIcon ? <div className="global-table-empty-icon">{emptyIcon}</div> : null}
                  <div>{emptyMessage}</div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {enablePagination && totalPages > 1 && (
        <div className="global-table-pagination">
          <button
            type="button"
            className="global-table-page-btn"
            onClick={() => setPage(normalizedPage - 1)}
            disabled={normalizedPage <= 1}
          >
            Anterior
          </button>

          <div className="global-table-page-info">
            <div>Pagina {normalizedPage} de {totalPages}</div>
            <div className="global-table-page-range">
              Mostrando {rangeStart}-{rangeEnd} de {totalRows}
            </div>
          </div>

          <button
            type="button"
            className="global-table-page-btn"
            onClick={() => setPage(normalizedPage + 1)}
            disabled={normalizedPage >= totalPages}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
};

export default GlobalDataTable;
